import {gql} from '@apollo/client';
import React from 'react';

import {assert} from 'Asserts';
import {SchemaCollaborator} from 'auth/PermissionConstants';
import {SchemaPolicy} from 'auth/PolicyConstants';
import BenchlingConfig from 'BenchlingConfig';
import {useSchemaCollaboratorsSection} from 'collaborator-components/useSchemaCollaboratorsSection';
import {writeFragmentsToCache} from 'graphql-client/ApolloWrappers';
import {QueryResultType, useBenchlingQuery} from 'graphql-client/BenchlingQuery';
import {getFragmentWrapper} from 'graphql-client/declarative-translations/external/getFragmentWrapper';
import ExtractByTypename from 'graphql-client/ExtractByTypename';
import {isInaccessible} from 'graphql-client/InaccessibleInfo';
import {SchemaWithOrgPermissionsSettings} from 'schemas/SchemaWithOrgPermissionsSettings';
import {CenteredHorizontalThrobber} from 'ui-library/HorizontalThrobber';

import {
  SchemaInterfacePermissionSettingsQuery,
  SchemaInterfacePermissionSettingsQueryVariables,
} from './__generated/SchemaInterfacePermissionSettingsQuery';
import {updateCollaborators} from './SchemaInterfaceDAO';

type SchemaInterface = ExtractByTypename<
  SchemaInterfacePermissionSettingsQuery['schemaInterface'],
  'SchemaInterfaceNode'
>;

export function isValidSchemaInterfacePolicy(policy: SchemaPolicy): boolean {
  return policy.id !== BenchlingConfig.PERMISSIONS_DEFAULT_SCHEMA_POLICIES?.create_policy_id;
}

export const SCHEMA_INTERFACE_PERMISSION_SETTINGS_QUERY = gql`
  query SchemaInterfacePermissionSettingsQuery($schemaInterfaceId: StrictString!)
  @skipAudit
  @engComponent(name: SCHEMAS) {
    schemaInterface(id: $schemaInterfaceId) {
      ... on PoliceableInterface {
        id
        permissions {
          ...SchemaPermissionsGraphQL
        }
        ...CollaboratorInfoGraphQL
      }
      ... on InaccessibleInfoNode {
        ...InaccessibleInfoGraphQL
      }
    }
  }
  ${getFragmentWrapper('CollaboratorInfo').fragment}
  ${getFragmentWrapper('SchemaPermissions').fragment}
  ${getFragmentWrapper('InaccessibleInfo').fragment}
`;

async function updateSchemaInterfaceCollaborators(
  interfaceId: string,
  data: {
    collaborators: Array<SchemaCollaborator>;
  }
): Promise<void> {
  const response = await updateCollaborators(interfaceId, data);
  writeFragmentsToCache(getFragmentWrapper('CollaboratorInfo'), [response.interface]);
}

export interface QueryingSchemaInterfacePermissionsSettingsProps {
  schemaInterfaceId: string;
}
function QueryingSchemaInterfacePermissionSettings({
  schemaInterfaceId,
}: QueryingSchemaInterfacePermissionsSettingsProps): JSX.Element | null {
  const result = useBenchlingQuery<
    SchemaInterfacePermissionSettingsQuery,
    SchemaInterfacePermissionSettingsQueryVariables
  >(SCHEMA_INTERFACE_PERMISSION_SETTINGS_QUERY, {
    variables: {schemaInterfaceId},
  });
  if (result.type === QueryResultType.ERROR) {
    return null;
  } else if (result.type === QueryResultType.PENDING) {
    return <CenteredHorizontalThrobber />;
  } else {
    assert(!isInaccessible(result.data.schemaInterface), 'Expected schema interface to be accessible.');
    return (
      <ExistingSchemaInterfacePermissionSettings
        schemaInterface={result.data.schemaInterface}
        refetch={async () => {
          await result.refetch();
        }}
      />
    );
  }
}

export function ExistingSchemaInterfacePermissionSettings({
  schemaInterface,
  refetch,
}: {
  schemaInterface: SchemaInterface;
  refetch: () => Promise<void>;
}): JSX.Element {
  const [{collaborators, minimalCollaborators}, handleUpdate] = useSchemaCollaboratorsSection(
    schemaInterface.collaboratorsJson
  );

  return (
    <SchemaWithOrgPermissionsSettings
      id={schemaInterface.id}
      isReadOnly={!schemaInterface.permissions.definitionEditable}
      collaborators={collaborators}
      onUpdate={handleUpdate}
      onSubmit={async () => {
        await updateSchemaInterfaceCollaborators(schemaInterface.id, {
          collaborators: minimalCollaborators,
        });
        await refetch();
      }}
      policyPredicate={isValidSchemaInterfacePolicy}
    />
  );
}

export default QueryingSchemaInterfacePermissionSettings;
